import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { SubmitButton } from "components";
import { Permission, PermissionAction, User } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const UserPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  user: User;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.user.id;

  const save = (user: User) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.users
        .saveUpdate(user)
        .then(() => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } else {
      ApiService.users
        .saveCreate(user)
        .then((response) => {
          const id = response;
          props.fetch(id);
          props.reroute(id, "view");
          listContext.loadList?.();

          // send user invitation email
          ApiService.users.sendInvitationEmail(response).then((response) => {
            dispatch(setAppToast(newToast("success")));
          });

          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.users.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const deactivate = (id: number) => {
    if (window.confirm("Are you sure you want to deactivate this user?. He/She will no longer be able to login.")) {
      ApiService.users.deactivate(id).then(() => {
        props.fetch(id);
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const activate = (id: number) => {
    if (window.confirm("Are you sure you want to activate this user?")) {
      ApiService.users.activate(id).then(() => {
        props.fetch(id);
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Delete) && !props.user.deactivated)
      menuItems.push({
        label: <Typography>Deactivate</Typography>,
        action: () => deactivate(id),
      });

    if (isAuthorized(props.rootPermission, PermissionAction.Update) && props.user.deactivated)
      menuItems.push({
        label: <Typography>Activate</Typography>,
        action: () => activate(id),
      });

    if (isAuthorized(props.rootPermission, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" spacing="sm">
        <Stack>
          <Typography variant="inherit">
            {id === 0 ? "New" : ""} {props.handle}
          </Typography>
          {props.user.deactivated && (
            <Typography color="error" variant="caption">
              Deactivated
            </Typography>
          )}
        </Stack>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}{" "}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          <UpdateButton onClick={() => props.reroute(id, "edit")} />
        )}

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.user)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
